import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle, Clock, XCircle, AlertTriangle, User, Calendar, MapPin } from 'lucide-react';

type SignatureStatusType = 'PENDING' | 'SIGNED' | 'DECLINED' | 'EXPIRED';

interface Signature {
  id: string;
  signerRole: string;
  signerName: string;
  signerEmail: string;
  status: SignatureStatusType;
  signedAt?: string;
  ipAddress?: string;
  geolocation?: {
    latitude: number;
    longitude: number;
  };
  order: number;
}

interface SignatureStatusProps {
  signatures: Signature[];
  showTimeline?: boolean;
  showDetails?: boolean;
  className?: string;
}

const STATUS_CONFIG: Record<SignatureStatusType, { icon: typeof CheckCircle; color: string; bgColor: string; label: string }> = {
  PENDING: {
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    label: 'Pendente',
  },
  SIGNED: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Assinado',
  },
  DECLINED: {
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Recusado',
  },
  EXPIRED: {
    icon: AlertTriangle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    label: 'Expirado',
  },
};

const ROLE_LABELS: Record<string, string> = {
  ORIGINADOR: 'Originador',
  TENANT: 'Empresa',
  CUSTODIANTE: 'Custodiante',
  INVESTIDOR: 'Investidor',
  TESTEMUNHA: 'Testemunha',
  REPRESENTANTE: 'Representante',
};

export function SignatureStatus({
  signatures,
  showTimeline = true,
  showDetails = false,
  className = '',
}: SignatureStatusProps) {
  const sortedSignatures = [...signatures].sort((a, b) => a.order - b.order);
  const signedCount = signatures.filter(s => s.status === 'SIGNED').length;
  const totalCount = signatures.length;

  return (
    <div className={className}>
      {/* Progress summary */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Progresso das Assinaturas
          </span>
          <span className="text-sm text-gray-500">
            {signedCount} de {totalCount}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${(signedCount / totalCount) * 100}%` }}
          />
        </div>
      </div>

      {/* Signatures list */}
      <div className="space-y-3">
        {sortedSignatures.map((signature, index) => {
          const config = STATUS_CONFIG[signature.status];
          const Icon = config.icon;
          const isLast = index === sortedSignatures.length - 1;

          return (
            <div key={signature.id} className="relative">
              {/* Timeline connector */}
              {showTimeline && !isLast && (
                <div className="absolute left-5 top-12 w-0.5 h-full bg-gray-200" />
              )}

              <div className={`flex gap-4 p-4 rounded-lg border ${
                signature.status === 'SIGNED' 
                  ? 'border-green-200 bg-green-50' 
                  : signature.status === 'PENDING'
                  ? 'border-yellow-200 bg-yellow-50'
                  : 'border-gray-200 bg-gray-50'
              }`}>
                {/* Status icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${config.color}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-900 truncate">
                        {signature.signerName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {ROLE_LABELS[signature.signerRole] || signature.signerRole}
                      </p>
                    </div>
                    <span className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
                      {config.label}
                    </span>
                  </div>

                  {/* Email */}
                  <p className="text-sm text-gray-500 mt-1 truncate">
                    {signature.signerEmail}
                  </p>

                  {/* Signed details */}
                  {signature.status === 'SIGNED' && signature.signedAt && (
                    <div className="mt-2 pt-2 border-t border-green-200">
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(signature.signedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </span>
                        {showDetails && signature.ipAddress && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            IP: {signature.ipAddress}
                          </span>
                        )}
                        {showDetails && signature.geolocation && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {signature.geolocation.latitude.toFixed(2)}, {signature.geolocation.longitude.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-xs text-gray-500">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const Icon = config.icon;
          return (
            <span key={status} className="flex items-center gap-1">
              <Icon className={`w-3 h-3 ${config.color}`} />
              {config.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
